
import React from 'react';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Settings, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ChatPanelHeaderProps {
  activeTab: string;
  setShowCompanySettings: (show: boolean) => void;
  setShowOptionsMenu: (show: boolean) => void;
  showOptionsMenu: boolean;
  setShowInsuranceOfferForm: (show: boolean) => void;
  setShowInsuranceComparisonForm: (show: boolean) => void;
  setShowVibeSignForm: (show: boolean) => void;
  setInput: (input: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  credits?: number | null;
  isBuilderPage?: boolean;
}

const ChatPanelHeader: React.FC<ChatPanelHeaderProps> = ({
  activeTab,
  setShowCompanySettings,
  setShowOptionsMenu,
  showOptionsMenu,
  setShowInsuranceOfferForm,
  setShowInsuranceComparisonForm,
  setShowVibeSignForm,
  setInput,
  textareaRef,
  credits,
  isBuilderPage = false
}) => {
  // Predefined email creation prompts
  const emailPrompts = [
    "Erstelle eine E-Mail für eine Vertragsverlängerung mit attraktiven Sonderkonditionen.",
    "Erstelle eine Willkommens-E-Mail für Neukunden mit wichtigen Informationen.",
    "Erstelle eine Newsletter-E-Mail mit den neuesten Angeboten und Versicherungstipps.",
    "Erstelle eine E-Mail für die Ankündigung neuer Versicherungsprodukte.",
    "Erstelle eine Follow-up E-Mail für Kunden nach einem Beratungsgespräch.",
    "Erstelle eine E-Mail mit Schadenspräventions-Tipps für die aktuelle Saison."
  ];

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }, 100);
    }
    
    // Dispatch a custom event to activate the chat tab if needed
    window.dispatchEvent(new CustomEvent('set-tab', { detail: { tab: 'chat' } }));
  };

  return (
    <div className="border-b border-white/5 bg-black/80 backdrop-blur-lg">
      <div className="flex items-center justify-between p-3">
        <Tabs defaultValue="chat" className="w-full">
          <TabsList className="chat-tabs-container w-full grid grid-cols-2">
            <TabsTrigger
              value="chat"
              className={`rounded-none px-8 py-2 ${
                activeTab === 'chat' ? 'chat-tab-active' : 'chat-tab-inactive'
              }`}
              onClick={() => window.dispatchEvent(new CustomEvent('set-tab', { detail: { tab: 'chat' } }))}
            >
              Chat
            </TabsTrigger>
            <TabsTrigger
              value="links"
              className={`rounded-none px-8 py-2 ${
                activeTab === 'links' ? 'chat-tab-active' : 'chat-tab-inactive'
              }`}
              onClick={() => window.dispatchEvent(new CustomEvent('set-tab', { detail: { tab: 'links' } }))}
            >
              Links
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex items-center gap-2">
          {credits !== undefined && (
            <Badge variant="outline" className="bg-black/60 border-jade/20 text-gray-300 rounded-none">
              {credits} Credits
            </Badge>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="neo-blur hover:bg-black/80 hover:border-jade/30 shadow-lg h-8 w-8 rounded-none">
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-black/90 border border-white/10 backdrop-blur-md shadow-xl rounded-none">
              <DropdownMenuItem 
                className="text-gray-300 hover:bg-black/60 focus:bg-black/60 cursor-pointer"
                onClick={() => setShowInsuranceOfferForm(true)}
              >
                Angebot erstellen
              </DropdownMenuItem>
              
              <DropdownMenuItem 
                className="text-gray-300 hover:bg-black/60 focus:bg-black/60 cursor-pointer"
                onClick={() => setShowVibeSignForm(true)}
              >
                VibeSign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button
            variant="outline"
            size="icon"
            className="neo-blur hover:bg-black/80 hover:border-jade/30 shadow-lg h-8 w-8 rounded-none"
            onClick={() => setShowCompanySettings(true)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Only show the prompt buttons on the start page (not the builder page) */}
      {activeTab === 'chat' && !isBuilderPage && (
        <div className="grid grid-cols-3 gap-2 p-3 bg-gray-950/50 border-t border-gray-800/30">
          {emailPrompts.map((prompt, index) => (
            <Button 
              key={index}
              variant="outline" 
              size="sm" 
              className="rounded-none bg-black/40 border border-gray-800 hover:bg-black hover:border-jade/30 transition-all text-xs text-left whitespace-normal h-auto py-2"
              onClick={() => handlePromptClick(prompt)}
            >
              {prompt.substring(0, 40)}...
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatPanelHeader;
